<template>
  <div
    v-if="useNotificationV2"
    class="fixed inset-0 overflow-visible z-[1000] pointer-events-none"
  >
    <div class="absolute bottom-0 pointer-events-auto">
      <div
        v-for="listNotification in notifications"
        :key="listNotification.uniqueId"
      >
        <div :class="containerClassesV2(listNotification.type)">
          <div :class="notificationClasses">
            <div class="flex gap-1 items-center">
              <TIcon v-if="icon" class="m-0" :icon="icon" />
              {{ listNotification?.message }}
            </div>
            <TIcon
              class="m-0"
              icon="close"
              @click.stop="closeIcon(listNotification.uniqueId)"
            />
          </div>
          <div v-if="listNotification?.description" class="pl-6 ml-2">
            {{ listNotification.description }}
          </div>
        </div>
      </div>
    </div>
    <div class="fixed top-24 right-4 pointer-events-auto">
      <div
        v-for="listPersistentNotification in persistentNotifications"
        :key="listPersistentNotification.uniqueId"
      >
        <div :class="containerClassesV2(listPersistentNotification.type)">
          <div :class="notificationClasses">
            <div class="flex gap-1 items-center">
              <TIcon v-if="persistentIcon" class="m-0" :icon="persistentIcon" />
              <span
                class="clickable cursor-pointer hover:underline"
                @click="handlePersistentContentClick"
                >{{ listPersistentNotification?.message }}</span
              >
            </div>
            <TIcon
              class="m-0"
              icon="close"
              @click.stop="
                closePersistentIcon(listPersistentNotification.uniqueId)
              "
            />
          </div>
          <div v-if="listPersistentNotification?.description" class="pl-6 ml-2">
            {{ listPersistentNotification.description }}
          </div>
        </div>
      </div>
    </div>
  </div>
  <div
    v-else
    class="fixed inset-0 overflow-visible z-[1000] pointer-events-none"
  >
    <div
      v-if="persistentNotification"
      :class="['pointer-events-auto', ...persistentContainerClasses]"
    >
      <div :class="notificationClasses">
        <div class="flex gap-1 items-center">
          <TIcon v-if="persistentIcon" class="m-0" :icon="persistentIcon" />
          <span
            class="clickable cursor-pointer hover:underline"
            @click="handlePersistentContentClick"
            >{{ persistentNotification?.message }}</span
          >
        </div>
        <TIcon class="m-0" icon="close" @click.stop="closePersistentIcon" />
      </div>
      <div v-if="persistentNotification?.description" class="pl-6 ml-2">
        {{ persistentNotification.description }}
      </div>
    </div>
    <div
      v-if="notification"
      class="pointer-events-auto"
      :class="containerClasses"
    >
      <div :class="notificationClasses">
        <div class="flex gap-1 items-center">
          <TIcon v-if="icon" class="m-0" :icon="icon" />
          {{ notification?.message }}
        </div>
        <TIcon class="m-0" icon="close" @click.stop="closeIcon" />
      </div>
      <div v-if="notification?.description" class="pl-6 ml-2">
        {{ notification.description }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import TIcon from '@thyme/nashville/src/components/icon/TIcon.vue'
import { storeToRefs } from 'pinia'
import { computed, defineComponent } from 'vue'
import { useNotificationStore } from '@/legacy/store/modules/notification'
import {
  notificationTypeToIcon,
  notificationTypeToColor,
  notificationClasses,
  NotificationType,
} from '@/legacy/types/notifications'
import { useFlagStore } from '@/store/flags/flags'

export default defineComponent({
  components: {
    TIcon,
  },
  props: {
    clickable: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['click'],
  setup(props, { emit }) {
    const store = useNotificationStore()
    const {
      notification,
      persistentNotification,
      notifications,
      persistentNotifications,
    } = storeToRefs(store)

    const { useNotificationV2 } = storeToRefs(useFlagStore())

    const icon = computed(
      () => notificationTypeToIcon[notification.value?.type ?? '']
    )
    const bgColor = computed(
      () => notificationTypeToColor[notification.value?.type ?? '']
    )

    const persistentIcon = computed(
      () => notificationTypeToIcon[persistentNotification.value?.type ?? '']
    )
    const persistentBgColor = computed(
      () => notificationTypeToColor[persistentNotification.value?.type ?? '']
    )

    const persistentClasses = computed(() => ['fixed', 'top-24', 'right-4'])

    const baseClasses = computed(() => [
      'rounded',
      'max-h-20',
      'flex-col',
      'p-3',
      'text-nash-neutral000',
      'flex',
      'w-700',
      'inset-x-0',
      'mb-2.5',
      'mx-2.5',
      'mt-2',
      'nash-drop-shadow',
    ])

    const containerClasses = computed(() => [
      ...baseClasses.value,
      bgColor.value,
      'absolute',
      'bottom-0',
      'z-[1000]',
    ])

    const persistentContainerClasses = computed(() => [
      ...baseClasses.value,
      ...persistentClasses.value,
      persistentBgColor.value,
    ])

    /**
     * Close the notification
     * @param notificationId
     */
    function closeIcon(notificationId?: string) {
      store.closeNotification(notificationId)
    }

    /**
     *
     * @param notificationId
     */
    function closePersistentIcon(notificationId?: string) {
      store.closePersistentNotification(notificationId)
    }

    /**
     * Handle click on the notification content
     * Emit click event only if notification is clickable
     *
     */
    function handlePersistentContentClick() {
      if (props.clickable) {
        emit('click')
      }
    }

    /**
     *
     * @param type
     */
    function containerClassesV2(type: NotificationType) {
      return [...baseClasses.value, notificationTypeToColor[type ?? '']]
    }

    return {
      notification,
      icon,
      persistentIcon,
      containerClasses,
      notificationClasses,
      closeIcon,
      persistentContainerClasses,
      persistentNotification,
      closePersistentIcon,
      handlePersistentContentClick,

      // V2 that supports multiple notifications
      useNotificationV2,
      notifications,
      persistentNotifications,
      notificationTypeToColor,
      containerClassesV2,
      persistentClasses,
    }
  },
})
</script>
